import React, { useCallback, useEffect, useRef, useState } from 'react';
import Card from '../components/Card';
import Button from '../components/Button';
import { inputStyle } from '../components/FormField';
import * as planningApi from '../services/productionPlanningApi';
import { formatDate } from '../utils/dateFormat';

const DAY_PRESETS = [
  { key: '15', label: '15 Days' },
  { key: '30', label: '30 Days' },
  { key: 'custom', label: 'Custom days…' },
];

const TREND_META = {
  rising: { label: '▲ Rising', color: '#15803d', bg: 'rgba(22,163,74,0.12)' },
  falling: { label: '▼ Falling', color: '#b91c1c', bg: 'rgba(220,38,38,0.12)' },
  stable: { label: '● Stable', color: '#475569', bg: 'rgba(100,116,139,0.14)' },
  new: { label: '★ New', color: '#0F6982', bg: 'rgba(20,135,166,0.14)' },
};

const fmt = (n) => Number(n || 0).toLocaleString('en-IN');

const PP_CSS = `
.pp-stats { display: flex; flex-wrap: wrap; gap: 12px; margin: 18px 0 18px; }
.pp-stat { flex: 1 1 170px; border: 1px solid var(--color-border); border-left: 4px solid var(--color-border-strong, #B6C0C6); border-radius: 8px; padding: 10px 14px; background: var(--color-surface, #fff); }
.pp-stat.accent { border-left-color: var(--color-accent, #1487A6); }
.pp-stat.good { border-left-color: #16a34a; }
.pp-stat.warn { border-left-color: #d97706; }
.pp-stat-label { font-size: 11px; letter-spacing: .04em; text-transform: uppercase; color: var(--color-text-muted); }
.pp-stat-value { font-size: 22px; font-weight: 700; line-height: 1.25; font-variant-numeric: tabular-nums; }
.pp-stat-sub { font-size: 11px; color: var(--color-text-muted); }
.pp-section { font-size: 13px; font-weight: 700; margin: 4px 0 8px; display: flex; align-items: center; gap: 8px; }
.pp-section small { font-weight: 400; color: var(--color-text-muted); }
.pp-wrap { border: 1px solid var(--color-border); border-radius: 10px; overflow: auto; margin-bottom: 18px; box-shadow: 0 1px 2px rgba(15,23,42,.06); }
.pp-table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 12.5px; }
.pp-table th { position: sticky; top: 0; z-index: 1; background: var(--color-structural, #1A1F26); color: var(--color-structural-text, #E7E9EA); font-size: 11px; font-weight: 600; letter-spacing: .05em; text-transform: uppercase; padding: 9px 9px; text-align: left; white-space: normal; line-height: 1.25; vertical-align: bottom; border-bottom: 2px solid #0e1217; }
.pp-table th.num, .pp-table td.num { text-align: right; font-variant-numeric: tabular-nums; }
.pp-table th.key { background: var(--color-accent, #1487A6); color: #fff; border-bottom-color: var(--color-accent-hover, #0F6982); }
.pp-table td { padding: 8px 9px; border-bottom: 1px solid var(--color-border); white-space: nowrap; }
.pp-table tbody tr:nth-child(even) td { background: rgba(100,116,139,.07); }
.pp-table tbody tr:hover td { background: var(--color-accent-soft, #E1F1F4); }
.pp-table td.idx { color: var(--color-text-muted); text-align: center; width: 32px; }
.pp-table td.name { font-weight: 600; }
.pp-table td.strong { font-weight: 700; color: var(--color-accent, #1487A6); }
.pp-table tr.pp-total td { background: var(--color-accent-soft, #E1F1F4); font-weight: 700; border-top: 2px solid var(--color-structural, #1A1F26); border-bottom: none; position: sticky; bottom: 0; }
.pp-table.pp-freeze3 { --pp-fz-bg: var(--color-surface, #ffffff); }
.pp-table.pp-freeze3 thead th:nth-child(-n+3), .pp-table.pp-freeze3 tbody tr td:nth-child(-n+3) { position: sticky; box-sizing: border-box; }
.pp-table.pp-freeze3 thead th:nth-child(-n+3) { z-index: 4; }
.pp-table.pp-freeze3 tbody tr td:nth-child(-n+3) { z-index: 2; background: var(--pp-fz-bg); }
.pp-table.pp-freeze3 tbody tr:nth-child(even) td:nth-child(-n+3) { background: linear-gradient(rgba(100,116,139,.07), rgba(100,116,139,.07)), var(--pp-fz-bg); }
.pp-table.pp-freeze3 tbody tr:hover td:nth-child(-n+3) { background: var(--color-accent-soft, #E1F1F4); }
.pp-table.pp-freeze3 tbody tr.pp-total td:nth-child(-n+3) { background: var(--color-accent-soft, #E1F1F4); z-index: 3; }
.pp-table.pp-freeze3 thead th:nth-child(1), .pp-table.pp-freeze3 tbody tr td:nth-child(1) { left: 0px; width: 44px; min-width: 44px; max-width: 44px; }
.pp-table.pp-freeze3 thead th:nth-child(2), .pp-table.pp-freeze3 tbody tr td:nth-child(2) { left: 44px; width: 260px; min-width: 260px; max-width: 260px; overflow: hidden; text-overflow: ellipsis; }
.pp-table.pp-freeze3 thead th:nth-child(3), .pp-table.pp-freeze3 tbody tr td:nth-child(3) { left: 304px; width: 92px; min-width: 92px; max-width: 92px; }
.pp-table.pp-freeze3 thead th:nth-child(3), .pp-table.pp-freeze3 tbody tr td:nth-child(3) { box-shadow: 2px 0 0 var(--color-border, rgba(100,116,139,.35)); }
.pp-table.pp-freeze3 tbody tr td[colspan]:nth-child(1) { position: static; width: auto; min-width: 0; max-width: none; box-shadow: none; }
.pp-table.pp-freeze2 { --pp-fz-bg: var(--color-surface, #ffffff); }
.pp-table.pp-freeze2 thead th:nth-child(-n+2), .pp-table.pp-freeze2 tbody tr td:nth-child(-n+2) { position: sticky; box-sizing: border-box; }
.pp-table.pp-freeze2 thead th:nth-child(-n+2) { z-index: 4; }
.pp-table.pp-freeze2 tbody tr td:nth-child(-n+2) { z-index: 2; background: var(--pp-fz-bg); }
.pp-table.pp-freeze2 tbody tr:nth-child(even) td:nth-child(-n+2) { background: linear-gradient(rgba(100,116,139,.07), rgba(100,116,139,.07)), var(--pp-fz-bg); }
.pp-table.pp-freeze2 tbody tr:hover td:nth-child(-n+2) { background: var(--color-accent-soft, #E1F1F4); }
.pp-table.pp-freeze2 tbody tr.pp-total td:nth-child(-n+2) { background: var(--color-accent-soft, #E1F1F4); z-index: 3; }
.pp-table.pp-freeze2 thead th:nth-child(1), .pp-table.pp-freeze2 tbody tr td:nth-child(1) { left: 0px; width: 44px; min-width: 44px; max-width: 44px; }
.pp-table.pp-freeze2 thead th:nth-child(2), .pp-table.pp-freeze2 tbody tr td:nth-child(2) { left: 44px; width: 120px; min-width: 120px; max-width: 120px; }
.pp-table.pp-freeze2 thead th:nth-child(2), .pp-table.pp-freeze2 tbody tr td:nth-child(2) { box-shadow: 2px 0 0 var(--color-border, rgba(100,116,139,.35)); }
.pp-table.pp-freeze2 tbody tr td[colspan]:nth-child(1) { position: static; width: auto; min-width: 0; max-width: none; box-shadow: none; }
.pp-pill { display: inline-block; padding: 2px 9px; border-radius: 999px; font-size: 11px; font-weight: 600; }
.pp-share { display: flex; align-items: center; gap: 6px; justify-content: flex-end; min-width: 108px; }
.pp-bar { flex: 0 0 52px; height: 7px; border-radius: 4px; background: rgba(100,116,139,.2); overflow: hidden; }
.pp-bar > span { display: block; height: 100%; border-radius: 4px; background: linear-gradient(90deg, var(--color-accent, #1487A6), #5cc0d8); }
.pp-share b { width: 44px; text-align: right; font-variant-numeric: tabular-nums; }
.pp-note { font-size: 12px; color: var(--color-text-muted); line-height: 1.55; margin: 0; }
`;

function Stat({ label, value, sub, tone, onClick }) {
  const click = onClick ? { role: 'button', tabIndex: 0, onClick, onKeyDown: (e) => { if (e.key === 'Enter') onClick(); }, style: { cursor: 'pointer' } } : {};
  return (
    <div className={`pp-stat${tone ? ` ${tone}` : ''}`} {...click}>
      <div className="pp-stat-label">{label}</div>
      <div className="pp-stat-value">{value}</div>
      {sub && <div className="pp-stat-sub">{sub}</div>}
    </div>
  );
}

function ShareCell({ pct }) {
  const v = Number(pct) || 0;
  return (
    <div className="pp-share">
      <div className="pp-bar"><span style={{ width: `${Math.min(100, v)}%` }} /></div>
      <b>{v.toFixed(1)}%</b>
    </div>
  );
}

/* ---------- Production Calendar (click a day to open its full detail) ---------- */

const PC_CSS = `
.pc-head { display: flex; flex-wrap: wrap; gap: 10px 16px; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.pc-legend { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 12px; font-size: 11px; color: var(--color-text-muted); }
.pc-key { display: inline-flex; align-items: center; gap: 5px; white-space: nowrap; }
.pc-key i, .pc-cell.lv0, .pc-cell.lv1, .pc-cell.lv2, .pc-cell.lv3, .pc-cell.lv4 { --bg: #E8EDF1; --fg: #26384A; --ac: #9FB1C0; }
.pc-key i { display: inline-block; width: 14px; height: 14px; border-radius: 4px; background: var(--bg); border: 1px solid var(--ac); }
.pc-key i.lv0, .pc-cell.lv0 { --bg: #FBE3DD; --fg: #8E2A14; --ac: #E2735A; }
.pc-key i.lv1, .pc-cell.lv1 { --bg: #FCEFD2; --fg: #7A4E00; --ac: #E3A52E; }
.pc-key i.lv2, .pc-cell.lv2 { --bg: #E8EDF1; --fg: #26384A; --ac: #8FA3B5; }
.pc-key i.lv3, .pc-cell.lv3 { --bg: #D5EFE3; --fg: #0B5F42; --ac: #3FAF86; }
.pc-key i.lv4, .pc-cell.lv4 { --bg: #1F8A67; --fg: #FFFFFF; --ac: #136B4F; }
.pc-keynote { color: var(--color-text-muted); }
.pc-dow, .pc-week { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); gap: 6px; }
.pc-dow { margin-bottom: 6px; }
.pc-dow div { text-align: center; font-size: 11px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; color: var(--color-text-muted); }
.pc-week { margin-bottom: 6px; }
.pc-cell { position: relative; min-width: 0; min-height: 76px; border-radius: 8px; border: 1px solid transparent; padding: 7px 8px; display: flex; flex-direction: column; justify-content: space-between; text-align: left; font: inherit; background: rgba(100,116,139,.06); color: inherit; overflow: hidden; }
.pc-cell.has { background: var(--bg); color: var(--fg); border-color: var(--ac); border-left-width: 4px; cursor: pointer; transition: transform .08s, box-shadow .08s; }
.pc-cell.empty { background: transparent; border: 1px dashed var(--color-border); opacity: .7; cursor: default; }
.pc-cell.has:hover, .pc-cell.has:focus-visible { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(15,23,42,.2); outline: 2px solid var(--color-accent, #1487A6); outline-offset: 1px; }
.pc-cell.today { box-shadow: 0 0 0 2px var(--color-structural, #1A1F26); }
.pc-day { font-size: 11px; font-weight: 600; opacity: .8; }
.pc-day b { font-size: 12px; }
.pc-qty { display: block; white-space: nowrap; font-size: 15px; font-weight: 700; font-variant-numeric: tabular-nums; line-height: 1.15; }
.pc-sub { display: block; font-size: 10px; opacity: .85; white-space: nowrap; }
.pc-month { grid-column: 1 / -1; font-size: 12px; font-weight: 700; margin: 10px 0 3px; color: var(--color-text-secondary); }
.pc-overlay { position: fixed; inset: 0; z-index: 1000; overflow: auto; background: var(--color-bg, var(--color-surface-alt, #eef1f4)); padding: 20px clamp(12px, 3vw, 36px) 40px; }
.pc-bar { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; margin-bottom: 4px; }
.pc-bar h1 { font-size: 20px; margin: 0; flex: 1 1 auto; }
.pc-grid2 { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 18px; }
@media (max-width: 700px) {
  /* phone: one row per day so the full production number is always readable */
  .pc-dow { display: none; }
  .pc-week { display: block; margin-bottom: 0; }
  .pc-week > div:not(.pc-cell) { display: none; }
  .pc-cell { display: flex; width: 100%; box-sizing: border-box; flex-direction: row; align-items: center; min-height: 42px; padding: 6px 12px 6px 10px; margin-bottom: 5px; border-radius: 7px; }
  .pc-cell.has { border-left-width: 5px; }
  .pc-cell.empty { min-height: 28px; padding: 3px 12px; }
  .pc-cell.empty::after { content: 'no production'; font-size: 11px; margin-left: auto; }
  .pc-day { flex: 0 0 78px; font-size: 12px; }
  .pc-day::before { content: attr(data-dow) ' '; font-weight: 600; opacity: .85; }
  .pc-day b { font-size: 14px; }
  .pc-cell.has > span:last-child { display: flex; flex: 1; flex-direction: row-reverse; align-items: center; justify-content: space-between; gap: 8px; }
  .pc-cell br { display: none; }
  .pc-qty { font-size: 16px; }
  .pc-sub { font-size: 11px; }
}
`;

const PC_LEVELS = ['Very low', 'Low', 'Normal', 'Good', 'High'];
const pcPad = (n) => String(n).padStart(2, '0');
const pcKey = (d) => `${d.getFullYear()}-${pcPad(d.getMonth() + 1)}-${pcPad(d.getDate())}`;
const pcParse = (s) => { const [y, m, d] = String(s).split('-').map(Number); return new Date(y, m - 1, d); };
const pcLong = (s) => `${pcParse(s).toLocaleDateString('en-IN', { weekday: 'long' })}, ${formatDate(pcParse(s))}`;
const PC_SPANS = [30, 60, 90, 180];

function PcTable({ cols, rows, total, empty }) {
  if (!rows.length) return <p className="pp-note">{empty || 'No data.'}</p>;
  return (
    <div className="pp-wrap" style={{ marginBottom: 0 }}>
      <table className="pp-table">
        <thead>
          <tr>
            <th style={{ width: 32, textAlign: 'center' }}>#</th>
            {cols.map((c) => <th key={c.label} className={`${c.num ? 'num' : ''}${c.key ? ' key' : ''}`}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id != null ? r.id : i}>
              <td className="idx">{i + 1}</td>
              {cols.map((c) => (
                <td key={c.label} className={`${c.num ? 'num' : ''}${c.name ? ' name' : ''}${c.key ? ' strong' : ''}`}>{c.render ? c.render(r) : r[c.k]}</td>
              ))}
            </tr>
          ))}
          {total && (
            <tr className="pp-total">
              <td />
              {cols.map((c, i) => <td key={c.label} className={c.num ? 'num' : ''}>{i === 0 ? 'Total' : (total[c.label] != null ? total[c.label] : '')}</td>)}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function ProductionDayView({ date, dates, onClose, onPick }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const seq = useRef(0);

  useEffect(() => {
    const my = ++seq.current;
    setData(null); setError('');
    planningApi.getProductionDay(date)
      .then((d) => { if (seq.current === my) setData(d); })
      .catch(() => { if (seq.current === my) setError('Could not load this day.'); });
  }, [date]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const idx = dates.indexOf(date);
  const prevDate = idx > 0 ? dates[idx - 1] : null;
  const nextDate = idx >= 0 && idx < dates.length - 1 ? dates[idx + 1] : null;
  const s = data && data.summary;
  const pct = (v) => <ShareCell pct={v} />;

  return (
    <div className="pc-overlay" role="dialog" aria-modal="true" aria-label={`Production on ${date}`}>
      <div className="pc-bar">
        <Button variant="secondary" onClick={onClose}>← Back</Button>
        <h1>Production — {pcLong(date)}</h1>
        <Button variant="secondary" disabled={!prevDate} onClick={() => prevDate && onPick(prevDate)}>‹ Previous day</Button>
        <Button variant="secondary" disabled={!nextDate} onClick={() => nextDate && onPick(nextDate)}>Next day ›</Button>
      </div>

      {error && <p style={{ color: 'var(--color-danger, #b91c1c)' }}>{error}</p>}
      {!data && !error && <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p>}

      {data && s && s.entries === 0 && <p className="pp-note" style={{ marginTop: 16 }}>No production was recorded on this day.</p>}

      {data && s && s.entries > 0 && (
        <>
          <div className="pp-stats">
            <Stat label="Total produced (boxes)" value={fmt(s.totalBoxes)} tone="accent" />
            <Stat label="Designs" value={fmt(new Set(data.entries.map((e) => e.design)).size)} />
            <Stat label="Sizes" value={fmt(s.bySize.length)} />
            <Stat label="Locations" value={fmt(s.byLocation.length)} />
            <Stat label="Entries" value={fmt(s.entries)} />
            <Stat label="Premium / Standard / Commercial" value={`${fmt(s.pre)} / ${fmt(s.std)} / ${fmt(s.com)}`} />
          </div>

          <div className="pc-grid2">
            <div>
              <div className="pp-section">By size</div>
              <PcTable
                rows={s.bySize}
                cols={[
                  { label: 'Size', k: 'size', name: true },
                  { label: 'Entries', num: true, render: (r) => fmt(r.entries) },
                  { label: 'Boxes', num: true, render: (r) => fmt(r.boxes) },
                  { label: '%', num: true, key: true, render: (r) => pct(r.pct) },
                ]}
              />
            </div>
            <div>
              <div className="pp-section">By location</div>
              <PcTable
                rows={s.byLocation}
                cols={[
                  { label: 'Location', k: 'location', name: true },
                  { label: 'Entries', num: true, render: (r) => fmt(r.entries) },
                  { label: 'Boxes', num: true, render: (r) => fmt(r.boxes) },
                  { label: '%', num: true, key: true, render: (r) => pct(r.pct) },
                ]}
              />
            </div>
            <div>
              <div className="pp-section">By manufacturing status</div>
              <PcTable
                rows={s.byStatus}
                cols={[
                  { label: 'Status', k: 'status', name: true },
                  { label: 'Entries', num: true, render: (r) => fmt(r.entries) },
                  { label: 'Boxes', num: true, render: (r) => fmt(r.boxes) },
                  { label: '%', num: true, key: true, render: (r) => pct(r.pct) },
                ]}
              />
            </div>
          </div>

          <div className="pp-section" style={{ marginTop: 20 }}>By design <small>— what was produced on this day, with each design&apos;s share</small></div>
          <PcTable
            rows={s.byDesign}
            total={{ Entries: fmt(s.entries), Boxes: fmt(s.totalBoxes), '%': '100.0%' }}
            cols={[
              { label: 'Design', k: 'design', name: true },
              { label: 'Size', k: 'size' },
              { label: 'Entries', num: true, render: (r) => fmt(r.entries) },
              { label: 'Boxes', num: true, render: (r) => fmt(r.boxes) },
              { label: '%', num: true, key: true, render: (r) => pct(r.pct) },
            ]}
          />

          <div className="pp-section" style={{ marginTop: 20 }}>All entries <small>— every production line recorded on this day</small></div>
          <PcTable
            rows={data.entries}
            total={{ Premium: fmt(s.pre), Standard: fmt(s.std), Commercial: fmt(s.com), Total: fmt(s.totalBoxes) }}
            cols={[
              { label: 'Design', k: 'design', name: true },
              { label: 'Size', k: 'size' },
              { label: 'Category', k: 'category' },
              { label: 'Location', k: 'location' },
              { label: 'Status', k: 'status' },
              { label: 'Shade / Batch', render: (r) => r.shade || '—' },
              { label: 'Box pack', render: (r) => r.boxPack || '—' },
              { label: 'Premium', num: true, render: (r) => fmt(r.pre) },
              { label: 'Standard', num: true, render: (r) => fmt(r.std) },
              { label: 'Commercial', num: true, render: (r) => fmt(r.com) },
              { label: 'Total', num: true, key: true, render: (r) => fmt(r.total) },
            ]}
          />
        </>
      )}
    </div>
  );
}

// carries its own CSS so it looks the same when opened from other pages (Stock Analysis)
export function HoldView(props) {
  return (
    <>
      <style>{PP_CSS}</style>
      <style>{PC_CSS}</style>
      <HoldViewInner {...props} />
    </>
  );
}

function HoldViewInner({ data, onClose, initialSize }) {
  const normSize = (v) => String(v || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const lockedSize = initialSize
    ? ((data.rows || []).find((r) => normSize(r.size) === normSize(initialSize)) || {}).size || ''
    : '';
  const [size, setSize] = useState(lockedSize);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  // clicked a size card -> only that size's data; Total Box -> all sizes mixed
  const all = lockedSize ? (data.rows || []).filter((r) => r.size === lockedSize) : (data.rows || []);
  const sizes = Array.from(new Set(all.map((r) => r.size))).sort();
  const rows = size ? all.filter((r) => r.size === size) : all;
  const sizeTabs = (lockedSize ? [] : [{ k: '', label: 'All sizes', boxes: all.reduce((t, r) => t + r.total, 0) }]).concat(
    sizes.map((z) => ({ k: z, label: z, boxes: all.filter((r) => r.size === z).reduce((t, r) => t + r.total, 0) })).sort((a, b) => b.boxes - a.boxes),
  );
  const total = rows.reduce((t, r) => t + r.total, 0);
  const pctOf = (v) => (total > 0 ? Math.round((v / total) * 1000) / 10 : 0);
  const pct = (v) => <ShareCell pct={v} />;
  const dShort = (s) => (s ? formatDate(pcParse(s)) : '—');
  const group = (keyFn, mk) => {
    const m = new Map();
    rows.forEach((r) => {
      const k = keyFn(r);
      const g = m.get(k) || { ...mk(r), boxes: 0, entries: 0 };
      g.boxes += r.total; g.entries += 1;
      m.set(k, g);
    });
    return Array.from(m.values()).map((g) => ({ ...g, pct: pctOf(g.boxes) }));
  };
  const bySize = group((r) => r.size, (r) => ({ size: r.size })).sort((a, b) => b.boxes - a.boxes);
  const byDesign = group((r) => `${r.design}|${r.size}`, (r) => ({ design: r.design, size: r.size })).sort((a, b) => b.boxes - a.boxes);
  const byDate = group((r) => r.producedOn || '', (r) => ({ date: r.producedOn || '' })).sort((a, b) => b.date.localeCompare(a.date));
  const AGE = [['0–7 days', 0, 7], ['8–30 days', 8, 30], ['31–90 days', 31, 90], ['Over 90 days', 91, 100000]];
  const byAge = AGE.map(([label, lo, hi]) => {
    const part = rows.filter((r) => r.daysOnHold != null && r.daysOnHold >= lo && r.daysOnHold <= hi);
    const boxes = part.reduce((t, r) => t + r.total, 0);
    return { label, entries: part.length, boxes, pct: pctOf(boxes) };
  });
  const noRec = rows.filter((r) => r.daysOnHold == null);
  if (noRec.length) {
    const boxes = noRec.reduce((t, r) => t + r.total, 0);
    byAge.push({ label: 'No production record', entries: noRec.length, boxes, pct: pctOf(boxes) });
  }
  const oldest = rows.reduce((m, r) => (r.daysOnHold != null && r.daysOnHold > m ? r.daysOnHold : m), 0);
  const designCount = new Set(rows.map((r) => `${r.design}|${r.size}`)).size;
  const totals = { Batches: fmt(rows.length), Boxes: fmt(total) };

  return (
    <div className="pc-overlay" role="dialog" aria-modal="true" aria-label="Stock on hold">
      <div className="pc-bar">
        <Button variant="secondary" onClick={onClose}>← Back</Button>
        <div style={{ fontWeight: 700, fontSize: 16 }}>On Hold — current stock</div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {sizeTabs.map((t) => (
            <button
              key={t.k || 'all'}
              type="button"
              onClick={() => setSize(t.k)}
              style={{
                cursor: 'pointer', padding: '5px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                border: '1px solid var(--color-border)',
                background: size === t.k ? 'var(--color-accent, #1487A6)' : 'transparent',
                color: size === t.k ? '#fff' : 'inherit',
              }}
            >
              {t.label} · {fmt(t.boxes)}
            </button>
          ))}
        </div>
      </div>

      <div className="pp-stats" style={{ marginTop: 8 }}>
        <Stat label="Boxes on hold" value={fmt(total)} sub={data.snapshotDate ? `stock file of ${dShort(data.snapshotDate)}` : ''} tone="warn" />
        <Stat label="Batches (lines)" value={fmt(rows.length)} />
        <Stat label="Designs" value={fmt(designCount)} />
        <Stat label="Oldest hold" value={oldest ? `${fmt(oldest)} days` : '—'} sub="since production date" />
      </div>

      {rows.length === 0 && <p className="pp-note">Nothing is on hold{size ? ` for size ${size}` : ''}.</p>}

      {rows.length > 0 && (
        <>
          <div className="pp-section" style={{ marginTop: 14 }}>By size</div>
          <PcTable
            cols={[
              { label: 'Size', name: true, render: (r) => r.size },
              { label: 'Batches', num: true, render: (r) => fmt(r.entries) },
              { label: 'Boxes', num: true, key: true, render: (r) => fmt(r.boxes) },
              { label: '%', num: true, render: (r) => pct(r.pct) },
            ]}
            rows={bySize}
            total={totals}
          />

          <div className="pp-section" style={{ marginTop: 14 }}>By production date <small>— when the held material was produced</small></div>
          <PcTable
            cols={[
              { label: 'Date', name: true, render: (r) => (r.date ? dShort(r.date) : 'No production record') },
              { label: 'Batches', num: true, render: (r) => fmt(r.entries) },
              { label: 'Boxes', num: true, key: true, render: (r) => fmt(r.boxes) },
              { label: '%', num: true, render: (r) => pct(r.pct) },
            ]}
            rows={byDate}
            total={totals}
          />

          <div className="pp-section" style={{ marginTop: 14 }}>By age of hold</div>
          <PcTable
            cols={[
              { label: 'Age', name: true, render: (r) => r.label },
              { label: 'Batches', num: true, render: (r) => fmt(r.entries) },
              { label: 'Boxes', num: true, key: true, render: (r) => fmt(r.boxes) },
              { label: '%', num: true, render: (r) => pct(r.pct) },
            ]}
            rows={byAge.filter((r) => r.entries > 0)}
            total={totals}
          />

          {bySize.map((sz) => {
            const list = byDesign.filter((d) => d.size === sz.size)
              .map((d) => ({ ...d, pctSize: sz.boxes > 0 ? Math.round((d.boxes / sz.boxes) * 1000) / 10 : 0 }));
            const sizeTotal = { Batches: fmt(list.reduce((t, d) => t + d.entries, 0)), Boxes: fmt(sz.boxes) };
            return (
              <React.Fragment key={sz.size}>
                <div className="pp-section" style={{ marginTop: 14 }}>
                  Designs on hold — {sz.size} <small>— {fmt(list.length)} designs, {fmt(sz.boxes)} boxes</small>
                </div>
                <PcTable
                  cols={[
                    { label: 'Design', name: true, render: (r) => r.design },
                    { label: 'Batches', num: true, render: (r) => fmt(r.entries) },
                    { label: 'Boxes', num: true, key: true, render: (r) => fmt(r.boxes) },
                    { label: '% of size', num: true, render: (r) => pct(r.pctSize) },
                  ]}
                  rows={list}
                  total={sizeTotal}
                />
              </React.Fragment>
            );
          })}

          <div className="pp-section" style={{ marginTop: 14 }}>All held batches</div>
          <PcTable
            cols={[
              { label: 'Produced on', render: (r) => dShort(r.producedOn) },
              { label: 'Days', num: true, render: (r) => (r.daysOnHold != null ? fmt(r.daysOnHold) : '—') },
              { label: 'Design', name: true, render: (r) => r.design },
              { label: 'Size', render: (r) => r.size },
              { label: 'Category', render: (r) => r.category },
              { label: 'Batch / Shade', render: (r) => r.shade || '—' },
              { label: 'Box pack', render: (r) => r.boxPack || '—' },
              { label: 'Location', render: (r) => r.location },
              { label: 'Pre', num: true, render: (r) => fmt(r.pre) },
              { label: 'Std', num: true, render: (r) => fmt(r.std) },
              { label: 'Com', num: true, render: (r) => fmt(r.com) },
              { label: 'Boxes', num: true, key: true, render: (r) => fmt(r.total) },
            ]}
            rows={[...rows].sort((a, b) => a.size.localeCompare(b.size, undefined, { numeric: true }) || b.total - a.total)}
            total={{ Boxes: fmt(total), Pre: fmt(rows.reduce((t, r) => t + r.pre, 0)), Std: fmt(rows.reduce((t, r) => t + r.std, 0)), Com: fmt(rows.reduce((t, r) => t + r.com, 0)) }}
          />
          <p className="pp-note" style={{ marginTop: 10 }}>
            On hold = stock lines whose MFG status is HOLD in the latest stock file. Produced on = the date the same design and batch/shade
            was produced (a HOLD production entry if there is one, otherwise any production entry of that batch).
          </p>
        </>
      )}
    </div>
  );
}

function ProductionCalendar({ reloadKey }) {
  const [span, setSpan] = useState(60);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDate, setOpenDate] = useState(null);
  const [hold, setHold] = useState(null);
  const [holdOpen, setHoldOpen] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const my = ++seq.current;
    setLoading(true); setError('');
    planningApi.getProductionCalendar(span)
      .then((rows) => { if (seq.current === my) { setItems(rows); setLoading(false); } })
      .catch(() => { if (seq.current === my) { setError('Could not load the production calendar.'); setLoading(false); } });
  }, [span, reloadKey]);

  useEffect(() => {
    let live = true;
    planningApi.getProductionHold()
      .then((h) => { if (live) setHold(h); })
      .catch(() => { if (live) setHold(null); });
    return () => { live = false; };
  }, [reloadKey]);

  const byDate = new Map(items.map((i) => [i.date, i]));
  const dates = items.map((i) => i.date).sort();
  const total = items.reduce((t, i) => t + i.boxes, 0);
  const best = items.reduce((b, i) => (i.boxes > (b ? b.boxes : -1) ? i : b), null);
  const todayKey = pcKey(new Date());

  const avg = items.length ? total / items.length : 0;
  const level = (b) => {
    const r = avg > 0 ? b / avg : 1;
    if (r < 0.6) return 0;
    if (r < 0.85) return 1;
    if (r < 1.15) return 2;
    if (r < 1.4) return 3;
    return 4;
  };

  const start = new Date();
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - span);
  const gridStart = new Date(start);
  gridStart.setDate(gridStart.getDate() - gridStart.getDay());
  const end = new Date();
  end.setHours(0, 0, 0, 0);

  const cells = [];
  for (let c = new Date(gridStart); c <= end; c.setDate(c.getDate() + 1)) cells.push(new Date(c));
  // every month gets its own block of weeks, so a day always sits under its own month heading
  const weeks = [];
  const monthOf = (d) => d.getFullYear() * 12 + d.getMonth();
  const inRange = cells.filter((d) => d >= start && d <= end);
  const monthKeys = Array.from(new Set(inRange.map(monthOf)));
  monthKeys.forEach((mk) => {
    const days = inRange.filter((d) => monthOf(d) === mk);
    const first = days[0];
    const last = days[days.length - 1];
    const lead = new Date(first);
    lead.setDate(lead.getDate() - lead.getDay());
    const wk = [];
    for (let c = new Date(lead); c <= last; c.setDate(c.getDate() + 1)) wk.push(new Date(c));
    for (let i = 0; i < wk.length; i += 7) {
      const w = wk.slice(i, i + 7);
      w.monthKey = mk;
      w.monthDate = first;
      weeks.push(w);
    }
  });

  let lastMonth = '';
  return (
    <div>
      <style>{PC_CSS}</style>
      {holdOpen && hold && <HoldView data={hold} onClose={() => setHoldOpen(false)} />}
      <div className="pc-head">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label htmlFor="pc-span" style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Show last</label>
          <select id="pc-span" value={span} onChange={(e) => setSpan(Number(e.target.value))} style={{ ...inputStyle, width: 'auto' }}>
            {PC_SPANS.map((d) => <option key={d} value={d}>{d} days</option>)}
          </select>
        </div>
        <div className="pc-legend">
          {PC_LEVELS.map((n, lv) => <span key={n} className="pc-key"><i className={`lv${lv}`} />{n}</span>)}
          <span className="pc-keynote">vs. average day ({fmt(Math.round(avg))} boxes) · click a day for details</span>
        </div>
      </div>

      {error && <p style={{ color: 'var(--color-danger, #b91c1c)' }}>{error}</p>}
      {loading && <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p>}

      {!loading && !error && items.length === 0 && (
        <p style={{ color: 'var(--color-text-muted)' }}>No production recorded in the last {span} days.</p>
      )}

      {!loading && items.length > 0 && (
        <>
          <div className="pp-stats" style={{ marginTop: 4 }}>
            <Stat label={`Produced — last ${span} days`} value={fmt(total)} sub="boxes" tone="accent" />
            <Stat label="Days with production" value={fmt(items.length)} />
            <Stat label="Average per production day" value={fmt(Math.round(total / items.length))} sub="boxes" />
            {best && <Stat label="Best day" value={fmt(best.boxes)} sub={pcLong(best.date)} />}
            {hold && <Stat label="On Hold (current stock)" value={fmt(hold.totalBoxes)} sub={`${fmt(hold.entries)} batches · click for details`} tone="warn" onClick={() => setHoldOpen(true)} />}
          </div>

          <div className="pc-dow">{['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => <div key={d}>{d}</div>)}</div>
          {weeks.map((week) => {
            const mLabel = week.monthDate.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
            const showMonth = mLabel !== lastMonth;
            lastMonth = mLabel;
            return (
              <React.Fragment key={`${week.monthKey}-${pcKey(week[0])}`}>
                {showMonth && <div className="pc-month">{mLabel}</div>}
                <div className="pc-week">
                  {week.map((d) => {
                    const k = pcKey(d);
                    const e = byDate.get(k);
                    const outside = d < start || d > end || (d.getFullYear() * 12 + d.getMonth()) !== week.monthKey;
                    if (outside) return <div key={k} />;
                    if (!e) {
                      return (
                        <div key={k} className={`pc-cell empty${k === todayKey ? ' today' : ''}`} title={`${k}: no production`}>
                          <span className="pc-day" data-dow={d.toLocaleDateString('en-IN', { weekday: 'short' })}><b>{d.getDate()}</b></span>
                        </div>
                      );
                    }
                    return (
                      <button
                        type="button"
                        key={k}
                        className={`pc-cell has lv${level(e.boxes)}${k === todayKey ? ' today' : ''}`}
                        title={`${pcLong(k)}: ${fmt(e.boxes)} boxes (${avg > 0 ? Math.round((e.boxes / avg) * 100) : 100}% of average), ${e.designs} designs — click for details`}
                        onClick={() => setOpenDate(k)}
                      >
                        <span className="pc-day" data-dow={d.toLocaleDateString('en-IN', { weekday: 'short' })}><b>{d.getDate()}</b></span>
                        <span>
                          <span className="pc-qty">{fmt(e.boxes)}</span>
                          <br />
                          <span className="pc-sub">{e.designs} design{e.designs === 1 ? '' : 's'}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </React.Fragment>
            );
          })}
        </>
      )}

      {openDate && <ProductionDayView date={openDate} dates={dates} onClose={() => setOpenDate(null)} onPick={setOpenDate} />}
    </div>
  );
}

export default function ProductionPlanningPage() {
  const [dayMode, setDayMode] = useState('30');
  const [customDays, setCustomDays] = useState('45');
  const [sizeId, setSizeId] = useState('');
  const [boxesInput, setBoxesInput] = useState('');
  const [plan, setPlan] = useState({ items: [], sizes: [], sizeSummary: [], summary: null });
  const [calendar, setCalendar] = useState([]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const requestSeq = useRef(0);

  const customN = parseInt(customDays, 10);
  const daysValid = dayMode !== 'custom' || (customN >= 1 && customN <= 365);
  const days = dayMode === 'custom' ? customN : Number(dayMode);
  const boxes = Math.max(0, parseInt(boxesInput, 10) || 0);

  useEffect(() => {
    planningApi.getCalendar(60).then(setCalendar).catch(() => {});
  }, []);

  const loadPlan = useCallback(async () => {
    if (!daysValid) return;
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;
    setLoading(true);
    try {
      const data = await planningApi.getSalesBasedPlan({ days, sizeId, boxes });
      if (seq !== requestSeq.current) return; // a newer request superseded this one
      setPlan(data);
      setError(null);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setError(err.message || 'Could not load the production plan.');
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [days, daysValid, sizeId, boxes]);

  useEffect(() => {
    const t = setTimeout(loadPlan, 400);
    return () => clearTimeout(t);
  }, [loadPlan]);

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      await planningApi.refreshPlan();
      const cal = await planningApi.getCalendar(60);
      setCalendar(cal);
      await loadPlan();
    } catch (err) {
      setError(err.message || 'Refresh failed.');
    } finally {
      setRefreshing(false);
    }
  };

  const items = plan.items || [];
  const sizeSummary = plan.sizeSummary || [];
  const summary = plan.summary;
  const sizeList = plan.sizes || [];
  const hasBoxPlan = summary && summary.plannedBoxes !== null && summary.plannedBoxes !== undefined;
  const periodLabel = daysValid ? `${days} Day${days === 1 ? '' : 's'}` : '—';
  const selectedSize = sizeList.find((s) => String(s.size_id) === String(sizeId));
  const acc = summary && summary.accuracy;

  const trendPill = (p) => {
    const tm = TREND_META[p.trend] || TREND_META.stable;
    return <span className="pp-pill" style={{ color: tm.color, background: tm.bg }}>{tm.label}</span>;
  };

  const detailCols = [
    { label: 'Design', cls: 'name', render: (p) => p.designName },
    { label: 'Size', render: (p) => p.sizeName || '—' },
    { label: 'Sales 6M', num: true, render: (p) => fmt(p.sales6m), total: (s) => fmt(s.sales6m) },
    { label: 'Sales 30D', num: true, render: (p) => fmt(p.sales30d), total: (s) => fmt(s.sales30d) },
    { label: 'Trend', render: trendPill },
    { label: 'Avg / Day', num: true, render: (p) => p.avgPerDay },
    { label: `Predicted (${periodLabel})`, num: true, render: (p) => fmt(p.predictedQty), total: (s) => fmt(s.predictedQty) },
    { label: 'Pending Orders', num: true, render: (p) => fmt(p.pendingOrderQty), total: (s) => fmt(s.pendingOrderQty) },
    { label: 'Current Stock', num: true, render: (p) => fmt(p.currentStock), total: (s) => fmt(s.currentStock) },
    { label: 'Production Needed', num: true, key: !hasBoxPlan, strong: !hasBoxPlan, render: (p) => fmt(p.productionNeeded), total: (s) => fmt(s.productionNeeded) },
  ];
  if (hasBoxPlan) {
    detailCols.push({ label: 'Planned Boxes', num: true, key: true, strong: true, render: (p) => fmt(p.plannedBoxes), total: (s) => fmt(s.plannedBoxes) });
  }
  if (items.some((p) => p.unlockRank)) {
    detailCols.push({ label: 'Unlock #', num: true, key: true, render: (p) => (p.unlockRank ? p.unlockRank : '—'), total: () => '' });
    detailCols.push({ label: 'Unlocks (boxes)', num: true, key: true, strong: true, render: (p) => (p.unlockBoxes ? fmt(p.unlockBoxes) : '—'), total: (s) => fmt(s.unlockBoxes) });
  }
  if (items.some((p) => p.readyBy)) {
    detailCols.push({ label: 'Ready by', num: true, key: true, render: (p) => (p.readyBy ? `${formatDate(`${p.readyBy}T00:00:00`)} (${p.readyInDays}d)` : '—'), total: () => '' });
  }
  detailCols.push({ label: '%', num: true, key: true, render: (p) => <ShareCell pct={p.sharePct} />, total: () => <ShareCell pct={items.length ? 100 : 0} /> });

  const sizeCols = [
    { label: 'Size', cls: 'name', render: (r) => r.sizeName },
    { label: 'Designs', num: true, render: (r) => fmt(r.designCount) },
    { label: 'Sales 6M', num: true, render: (r) => fmt(r.sales6m) },
    { label: 'Sales 30D', num: true, render: (r) => fmt(r.sales30d) },
    { label: `Predicted (${periodLabel})`, num: true, render: (r) => fmt(r.predictedQty) },
    { label: 'Pending Orders', num: true, render: (r) => fmt(r.pendingOrderQty) },
    { label: 'Current Stock', num: true, render: (r) => fmt(r.currentStock) },
    { label: 'Production Needed', num: true, key: !hasBoxPlan, strong: !hasBoxPlan, render: (r) => fmt(r.productionNeeded) },
  ];
  if (hasBoxPlan) sizeCols.push({ label: 'Planned Boxes', num: true, key: true, strong: true, render: (r) => fmt(r.plannedBoxes) });
  sizeCols.push({ label: '%', num: true, key: true, render: (r) => <ShareCell pct={r.sharePct} /> });

  const thCls = (c) => [c.num ? 'num' : '', c.key ? 'key' : ''].filter(Boolean).join(' ');
  const tdCls = (c) => [c.num ? 'num' : '', c.cls || '', c.strong ? 'strong' : ''].filter(Boolean).join(' ');

  return (
    <div>
      <style>{PP_CSS}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>Production Planning</h1>
          <p style={{ color: 'var(--color-text-secondary)' }}>
            Size and design wise production plan, predicted from the last 6 months of actual
            sales (dispatched boxes) — recent months count more, so what is selling now leads the plan.
          </p>
        </div>
        <Button variant="secondary" onClick={handleRefresh} disabled={refreshing}>
          {refreshing ? 'Regenerating…' : 'Regenerate plan'}
        </Button>
      </div>

      {error && <p style={{ color: 'var(--color-danger)' }}>{error}</p>}

      <Card title="Production Plan">
        <div className="kpi-row" style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)', alignItems: 'flex-end', marginBottom: 'var(--space-3)' }}>
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', gap: 4 }}>
            Size
            <select value={sizeId} onChange={(e) => setSizeId(e.target.value)} style={inputStyle}>
              <option value="">All sizes</option>
              {sizeList.map((s) => (
                <option key={s.size_id} value={String(s.size_id)}>{s.name}</option>
              ))}
            </select>
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', gap: 4 }}>
            Prediction days
            <select value={dayMode} onChange={(e) => setDayMode(e.target.value)} style={inputStyle}>
              {DAY_PRESETS.map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
          </label>

          {dayMode === 'custom' && (
            <label style={{ display: 'flex', flexDirection: 'column', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', gap: 4 }}>
              Number of days (1 – 365)
              <input
                type="number" min="1" max="365" step="1" value={customDays}
                onChange={(e) => setCustomDays(e.target.value)}
                style={{ ...inputStyle, width: 120, borderColor: daysValid ? undefined : 'var(--color-danger)' }}
              />
            </label>
          )}

          <label style={{ display: 'flex', flexDirection: 'column', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', gap: 4 }}>
            Boxes to plan (optional)
            <input
              type="number" min="0" step="1" placeholder="e.g. 5000" value={boxesInput}
              onChange={(e) => setBoxesInput(e.target.value)}
              style={{ ...inputStyle, width: 150 }}
            />
          </label>
          {summary && summary.plantCapacity && (
            <Button variant="secondary" onClick={() => setBoxesInput(String(Math.round(summary.plantCapacity.perDay * days)))}>
              Auto: plant capacity × days
            </Button>
          )}
          {boxesInput !== '' && (
            <Button variant="secondary" onClick={() => setBoxesInput('')}>Clear boxes</Button>
          )}
        </div>

        {!daysValid && (
          <p style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)', marginTop: 0 }}>
            Enter days between 1 and 365.
          </p>
        )}

        {summary && (
          <div className="pp-stats">
            <Stat label="Sales — last 6 months (boxes)" value={fmt(summary.sales6m)} sub={summary.salesDataUpTo ? `dispatch data up to ${summary.salesDataUpTo}` : undefined} />
            <Stat label={`Predicted sales — ${periodLabel}`} value={fmt(summary.predictedQty)} />
            <Stat label="Production needed" value={fmt(summary.productionNeeded)} sub="after pending orders & stock" tone="accent" />
            {summary.plantCapacity && <Stat label="Plant capacity (auto)" value={`${fmt(summary.plantCapacity.perDay)} / day`} sub={`${summary.plantCapacity.methodLabel}${summary.plantCapacity.accuracyPct != null ? ` · ${summary.plantCapacity.accuracyPct}% accurate in back-test` : ''}`} />}
            {hasBoxPlan && <Stat label="Boxes planned" value={fmt(summary.plannedBoxes)} sub={selectedSize ? `size ${selectedSize.name}` : 'all sizes'} tone="accent" />}
            {acc && <Stat label="Accuracy — size-wise" value={`${acc.sizeAccuracyPct}%`} sub="back-test, 30-day plan" tone="good" />}
            {acc && <Stat label="Accuracy — design-wise" value={`${acc.accuracyPct}%`} sub={acc.methodLabel} tone="good" />}
          </div>
        )}

        {hasBoxPlan && summary.allocation && summary.allocation.basis && summary.allocation.basis !== 'productionNeeded' && (
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 0 }}>
            No design needs extra production right now, so the boxes are split by predicted sales
            {summary.allocation.basis === 'sales6m' ? ' (6-month sales)' : ''}.
          </p>
        )}

        {loading ? (
          <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p>
        ) : (
          <>
            {sizeSummary.length > 1 && (
              <>
                <div className="pp-section">Size-wise summary <small>— {sizeSummary.length} sizes</small></div>
                <div className="pp-wrap">
                  <table className="pp-table pp-freeze2">
                    <thead>
                      <tr>
                        <th className="num">#</th>
                        {sizeCols.map((c) => (<th key={c.label} className={thCls(c)}>{c.label}</th>))}
                      </tr>
                    </thead>
                    <tbody>
                      {sizeSummary.map((r, i) => (
                        <tr key={r.sizeId}>
                          <td className="idx">{i + 1}</td>
                          {sizeCols.map((c) => (<td key={c.label} className={tdCls(c)}>{c.render(r)}</td>))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <div className="pp-section">Design-wise plan <small>— {items.length} designs{selectedSize ? `, size ${selectedSize.name}` : ''}</small></div>
            <div className="pp-wrap" style={{ maxHeight: 600 }}>
              <table className="pp-table pp-freeze3">
                <thead>
                  <tr>
                    <th className="num">#</th>
                    {detailCols.map((c) => (<th key={c.label} className={thCls(c)}>{c.label}</th>))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((p, i) => (
                    <tr key={`${p.sizeId}|${p.designName}`}>
                      <td className="idx">{i + 1}</td>
                      {detailCols.map((c) => (<td key={c.label} className={tdCls(c)}>{c.render(p)}</td>))}
                    </tr>
                  ))}
                  {items.length > 0 && summary && (
                    <tr className="pp-total">
                      <td />
                      {detailCols.map((c, idx) => (
                        <td key={c.label} className={c.num ? 'num' : ''}>
                          {idx === 0 ? 'Total' : (c.total ? c.total(summary) : '')}
                        </td>
                      ))}
                    </tr>
                  )}
                  {items.length === 0 && (
                    <tr><td colSpan={detailCols.length + 1} style={{ padding: 24, color: 'var(--color-text-muted)' }}>No sales or pending orders found for this filter.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        <p className="pp-note">
          How it works: the system back-tests several prediction methods on your own dispatch history (as if today were
          30, 60 and 90 days ago) and uses the most accurate one{acc ? ` — now: ${acc.methodLabel}` : ''}. Predicted = daily
          rate × days. Production needed = the larger of predicted sales and pending orders, minus current stock. Boxes to
          plan are split across the designs of the selected size in proportion to production needed. Sales = dispatches as counted in Dispatch Analysis
          (internal movements like SELF, resorting &amp; repacking and sample dispatch are not sales). The % column shows each design's share of the production
          plan shown{hasBoxPlan ? ' (planned boxes)' : ' (production needed)'}.
          {acc && acc.candidates ? ` Methods tested: ${acc.candidates.map((c) => `${c.label} ${c.accuracyPct}%`).join(' · ')}.` : ''}
        </p>
      </Card>

      <Card title="Production Calendar">
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginTop: 0 }}>
          Daily production recorded in the system. The colour shows how each day compares with the average production day. Click any day to see everything produced on it.
        </p>
        <ProductionCalendar reloadKey={calendar.length} />
      </Card>
    </div>
  );
}
